import { useRef, useCallback, useEffect } from 'react';
import type { DragMoveEvent } from '@dnd-kit/core';

const SCROLL_THRESHOLD = 80;
const SCROLL_SPEED = 12;

/**
 * Auto-scrolls the horizontal board strip and any vertically-scrollable
 * column under the pointer while dragging. Runs on a rAF loop so scrolling
 * continues even when the pointer is held stationary near an edge.
 */
export function useBoardAutoScroll<T extends HTMLElement = HTMLDivElement>() {
  const scrollRef = useRef<T>(null);
  const rafRef = useRef<number | null>(null);
  const pointerRef = useRef<{ x: number; y: number } | null>(null);

  const stop = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    pointerRef.current = null;
  }, []);

  const tick = useCallback(() => {
    rafRef.current = null;
    const container = scrollRef.current;
    const pointer = pointerRef.current;
    if (!container || !pointer) return;

    // Horizontal: board strip
    const rect = container.getBoundingClientRect();
    const distFromLeft = pointer.x - rect.left;
    const distFromRight = rect.right - pointer.x;

    if (distFromLeft < SCROLL_THRESHOLD && container.scrollLeft > 0) {
      const speed = SCROLL_SPEED * (1 - Math.max(distFromLeft, 0) / SCROLL_THRESHOLD);
      container.scrollBy({ left: -speed });
    } else if (
      distFromRight < SCROLL_THRESHOLD &&
      container.scrollLeft < container.scrollWidth - container.clientWidth
    ) {
      const speed = SCROLL_SPEED * (1 - Math.max(distFromRight, 0) / SCROLL_THRESHOLD);
      container.scrollBy({ left: speed });
    }

    // Vertical: column body under pointer
    const colBody = findScrollableAncestor(
      document.elementFromPoint(pointer.x, pointer.y),
      container,
    );
    if (colBody) {
      const cr = colBody.getBoundingClientRect();
      const distFromTop = pointer.y - cr.top;
      const distFromBottom = cr.bottom - pointer.y;
      if (distFromTop < SCROLL_THRESHOLD && colBody.scrollTop > 0) {
        const speed = SCROLL_SPEED * (1 - Math.max(distFromTop, 0) / SCROLL_THRESHOLD);
        colBody.scrollBy({ top: -speed });
      } else if (
        distFromBottom < SCROLL_THRESHOLD &&
        colBody.scrollTop < colBody.scrollHeight - colBody.clientHeight
      ) {
        const speed = SCROLL_SPEED * (1 - Math.max(distFromBottom, 0) / SCROLL_THRESHOLD);
        colBody.scrollBy({ top: speed });
      }
    }

    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const onDragMove = useCallback(
    (event: DragMoveEvent) => {
      const activator = event.activatorEvent as PointerEvent | MouseEvent | TouchEvent | undefined;
      if (!activator) return;
      const start = pointerFromEvent(activator);
      if (!start) return;
      pointerRef.current = {
        x: start.x + (event.delta?.x ?? 0),
        y: start.y + (event.delta?.y ?? 0),
      };
      if (rafRef.current === null) {
        rafRef.current = requestAnimationFrame(tick);
      }
    },
    [tick],
  );

  useEffect(() => stop, [stop]);

  return { scrollRef, onDragMove, onDragEnd: stop };
}

function pointerFromEvent(
  event: PointerEvent | MouseEvent | TouchEvent,
): { x: number; y: number } | null {
  if ('clientX' in event) return { x: event.clientX, y: event.clientY };
  const touch = event.touches[0] ?? event.changedTouches[0];
  if (touch) return { x: touch.clientX, y: touch.clientY };
  return null;
}

function findScrollableAncestor(
  start: Element | null,
  stopAt: Element,
): HTMLElement | null {
  let el: Element | null = start;
  while (el && el !== stopAt) {
    if (el instanceof HTMLElement) {
      const style = window.getComputedStyle(el);
      const overflowY = style.overflowY;
      if (
        (overflowY === 'auto' || overflowY === 'scroll') &&
        el.scrollHeight > el.clientHeight
      ) {
        return el;
      }
    }
    el = el.parentElement;
  }
  return null;
}
